<template>
    <div
        id="editDatafield_form"
        v-if="editedDatafield"
    >
        <h2 id="editDatafield_subtitle"></h2>

        <div id="f_name">
            <ui5-label for="name">{{ trans('product_types.datafield_edit_popup.key') }}</ui5-label>
            <ui5-input
                type="Text"
                :value="editedDatafield.name"
                @input="editedDatafield.name = $event.target.value"
                id="name"
                :readonly="editedDatafield.datafieldId ? true : false"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.name)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.name) }}
            </div>
        </div>

        <div
            id="l_label"
            class="datafieldTranslation"
        >
            <ui5-label for="label">{{ trans('product_types.datafield_edit_popup.external_name') }}</ui5-label>
            <translations-edit
                ref="translations-label"
                :translation-data="editedDatafield.translations.label"
            ></translations-edit>
        </div>

        <div id="c_mandatory">
            <ui5-label for="mandatory">{{ trans('product_types.datafield_edit_popup.mandatory') }}</ui5-label>
            <ui5-checkbox
                id="mandatory"
                :checked="editedDatafield.mandatory"
                @change="editedDatafield.mandatory = $event.target.checked"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.mandatory)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.mandatory) }}
            </div>
        </div>

        <div id="f_defaultValue">
            <ui5-label
                for="defaultValue"
                ref="defaultValueLabel"
            >
                {{ trans('product_types.datafield_edit_popup.default_value') }}
                <i
                    v-if="getTypeLabel(editedDatafield) == 'options'"
                    class="fa fa-question-circle"
                    @mouseout="$refs.tooltipDefaultValue.close()"
                    @mouseover="
                        $refs.tooltipDefaultValue.showAt(
                            $refs.defaultValueLabel
                        )
                    "
                />
            </ui5-label>
            <ui5-popover ref="tooltipDefaultValue">
                {{ trans('product_types.datafield_edit_popup.default_value_tooltip') }}
            </ui5-popover>
            <ui5-select
                v-if="getTypeLabel(editedDatafield) == 'options'"
                id="defaultValue"
                @change="
                    editedDatafield.defaultValue =
                        $event.detail.selectedOption.value
                "
            >
                <ui5-option
                    v-for="item in defaultValueOptions"
                    :key="item.value"
                    :selected="item.value == editedDatafield.defaultValue"
                    :value="item.value"
                >
                    {{ item.label }}
                </ui5-option>
            </ui5-select>
            <ui5-input
                v-else
                type="Text"
                id="defaultValue"
                :value="editedDatafield.defaultValue"
                @input="editedDatafield.defaultValue = $event.target.value"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.defaultValue)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.defaultValue) }}
            </div>
        </div>

        <div
            id="f_minValue"
            v-if="getTypeLabel(editedDatafield) == 'int'"
        >
            <ui5-label for="minValue">{{ trans('product_types.datafield_edit_popup.min_value') }}</ui5-label>
            <text-input
                type="number"
                id="minValue"
                v-model="editedDatafield.minValue"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.minValue)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.minValue) }}
            </div>
        </div>

        <div
            id="f_maxValue"
            v-if="getTypeLabel(editedDatafield) == 'int'"
        >
            <ui5-label for="maxValue">{{ trans('product_types.datafield_edit_popup.max_value') }}</ui5-label>
            <text-input
                type="number"
                id="maxValue"
                v-model="editedDatafield.maxValue"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.maxValue)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.maxValue) }}
            </div>
        </div>

        <div id="f_fieldOrder">
            <ui5-label for="fieldOrder">{{ trans('product_types.datafield_edit_popup.order') }}</ui5-label>
            <text-input
                type="number"
                id="fieldOrder"
                min="0"
                max="999"
                v-model="editedDatafield.fieldOrder"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.fieldOrder)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.fieldOrder) }}
            </div>
        </div>
        <div
            v-if="editedDatafield.datatype == 8 && !editedDatafield.popupFields"
            class="error"
        >
            {{ trans('product_types.datafield_edit_popup.prosite_error') }}
        </div>
        <div id="x_datatype">
            <ui5-label for="datatype">{{ trans('product_types.datafield_edit_popup.datatype') }}</ui5-label>
            <ui5-select
                id="datatype"
                @change="
                    editedDatafield.datatype =
                        $event.detail.selectedOption.value
                "
            >
                <ui5-option
                    v-for="item in dataTypeOptions"
                    :key="item.value"
                    :selected="item.value == editedDatafield.datatype"
                    :value="item.value"
                >
                    {{ item.label }}
                </ui5-option>
            </ui5-select>
            <div
                v-if="getErrorFromValidator(v.editedDatafield.datatype)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.datatype) }}
            </div>
        </div>

        <div
            id="f_subDataTypeId"
            v-if="showSubDataType"
        >
            <ui5-label for="subDataTypeId">{{ trans('product_types.datafield_edit_popup.subtype') }}</ui5-label>
            <ui5-select
                id="subDataTypeId"
                @change="
                    editedDatafield.subDataTypeId =
                        $event.detail.selectedOption.value
                "
            >
                <ui5-option
                    v-for="item in subDataTypeOptions"
                    :key="item.value"
                    :selected="item.value == editedDatafield.subDataTypeId"
                    :value="item.value"
                >
                    {{ item.label }}
                </ui5-option>
            </ui5-select>
            <div
                v-if="getErrorFromValidator(v.editedDatafield.subDataTypeId)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.subDataTypeId) }}
            </div>
        </div>

        <div id="f_codeFieldIndex">
            <ui5-label for="codeFieldIndex">{{ trans('product_types.datafield_edit_popup.code_index') }}</ui5-label>
            <text-input
                type="number"
                id="codeFieldIndex"
                min="0"
                max="999"
                v-model="editedDatafield.codeFieldIndex"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.codeFieldIndex)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.codeFieldIndex) }}
            </div>
        </div>

        <div id="c_inlineEdit">
            <ui5-label for="inlineEdit">{{ trans('product_types.datafield_edit_popup.inline_edit') }}</ui5-label>
            <ui5-checkbox
                id="inlineEdit"
                :checked="editedDatafield.inlineEdit"
                @change="editedDatafield.inlineEdit = $event.target.checked"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.inlineEdit)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.inlineEdit) }}
            </div>
        </div>

        <div id="f_cncIndex">
            <ui5-label for="cncIndex">{{ trans('product_types.datafield_edit_popup.cnc_index') }}</ui5-label>
            <text-input
                type="number"
                id="cncIndex"
                min="0"
                max="999"
                v-model="editedDatafield.cncIndex"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.cncIndex)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.cncIndex) }}
            </div>
        </div>

        <div id="c_hidden">
            <ui5-label for="hidden">{{ trans('product_types.datafield_edit_popup.hidden') }}</ui5-label>
            <ui5-checkbox
                id="hidden"
                :checked="editedDatafield.hidden"
                @change="editedDatafield.hidden = $event.target.checked"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.hidden)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.hidden) }}
            </div>
        </div>

        <div v-if="editedDatafield.datatype == 2">
            <ui5-label for="use_absolute_value" wrapping-type="Normal">{{ trans('product_types.datafield_edit_popup.absolute_value') }}</ui5-label>
            <ui5-checkbox
                id="use_absolute_value"
                :checked="editedDatafield.useAbsoluteValue"
                @change="editedDatafield.useAbsoluteValue = $event.target.checked"
            />
        </div>

        <div id="c_inUse">
            <ui5-label for="inUse">{{ trans('product_types.datafield_edit_popup.active') }}</ui5-label>
            <ui5-checkbox
                id="inUse"
                :checked="editedDatafield.inUse"
                @change="editedDatafield.inUse = $event.target.checked"
            />
            <div
                v-if="getErrorFromValidator(v.editedDatafield.inUse)"
                class="error"
            >
                {{ getErrorFromValidator(v.editedDatafield.inUse) }}
            </div>
        </div>

        <div class="prosite-info">
            <ui5-label>{{ trans('product_types.datafield_edit_popup.prosite_info') }}</ui5-label>
            <ui5-textarea
                :growing="true"
                :value="editedDatafield.prositeInfo"
                @input="editedDatafield.prositeInfo = $event.target.value"
            ></ui5-textarea>
        </div>

        <div
            id="datatype_table_fields"
            v-if="isTableType(getTypeLabel(editedDatafield))"
        >
            <div id="f_table">
                <ui5-label for="table">{{ trans('product_types.datafield_edit_popup.table_name') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="table"
                    :value="editedDatafield.table"
                    @input="editedDatafield.table = $event.target.value"
                />
                <div
                    v-if="getErrorFromValidator(v.editedDatafield.table)"
                    class="error"
                >
                    {{ getErrorFromValidator(v.editedDatafield.table) }}
                </div>
            </div>
            <div id="f_tableKey">
                <ui5-label for="tableKey">{{ trans('product_types.datafield_edit_popup.table_key') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableKey"
                    :value="editedDatafield.tableKey"
                    @input="editedDatafield.tableKey = $event.target.value"
                />
                <div
                    v-if="getErrorFromValidator(v.editedDatafield.tableKey)"
                    class="error"
                >
                    {{ getErrorFromValidator(v.editedDatafield.tableKey) }}
                </div>
            </div>
            <div id="f_tableField">
                <ui5-label for="tableField">{{ trans('product_types.datafield_edit_popup.table_field') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableField"
                    :value="editedDatafield.tableField"
                    @input="editedDatafield.tableField = $event.target.value"
                />
                <div
                    v-if="getErrorFromValidator(v.editedDatafield.tableField)"
                    class="error"
                >
                    {{ getErrorFromValidator(v.editedDatafield.tableField) }}
                </div>
            </div>
            <div id="f_tableFieldName">
                <ui5-label for="tableFieldName">{{ trans('product_types.datafield_edit_popup.table_field_name') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableFieldName"
                    :value="editedDatafield.tableFieldName"
                    @input="
                        editedDatafield.tableFieldName = $event.target.value
                    "
                />
                <div
                    v-if="
                        getErrorFromValidator(v.editedDatafield.tableFieldName)
                    "
                    class="error"
                >
                    {{
                        getErrorFromValidator(v.editedDatafield.tableFieldName)
                    }}
                </div>
            </div>
            <div id="f_tableField2">
                <ui5-label for="tableField2">{{ trans('product_types.datafield_edit_popup.table_field_2') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableField2"
                    :value="editedDatafield.tableField2"
                    @input="editedDatafield.tableField2 = $event.target.value"
                />
                <div
                    v-if="getErrorFromValidator(v.editedDatafield.tableField2)"
                    class="error"
                >
                    {{ getErrorFromValidator(v.editedDatafield.tableField2) }}
                </div>
            </div>
            <div id="f_tableFieldName2">
                <ui5-label for="tableFieldName2">{{ trans('product_types.datafield_edit_popup.table_field_name_2') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableFieldName2"
                    :value="editedDatafield.tableFieldName2"
                    @input="
                        editedDatafield.tableFieldName2 = $event.target.value
                    "
                />
                <div
                    v-if="
                        getErrorFromValidator(v.editedDatafield.tableFieldName2)
                    "
                    class="error"
                >
                    {{
                        getErrorFromValidator(v.editedDatafield.tableFieldName2)
                    }}
                </div>
            </div>
            <div id="f_tableFilter">
                <ui5-label for="tableFilter">{{ trans('product_types.datafield_edit_popup.table_filter') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableFilter"
                    :value="editedDatafield.tableFilter"
                    @input="editedDatafield.tableFilter = $event.target.value"
                />
                <div
                    v-if="getErrorFromValidator(v.editedDatafield.tableFilter)"
                    class="error"
                >
                    {{ getErrorFromValidator(v.editedDatafield.tableFilter) }}
                </div>
            </div>
            <div id="f_tableFormat">
                <ui5-label for="tableFormat">{{ trans('product_types.datafield_edit_popup.table_format') }}</ui5-label>
                <ui5-input
                    type="Text"
                    id="tableFormat"
                    :value="editedDatafield.tableFormat"
                    @input="editedDatafield.tableFormat = $event.target.value"
                />
                <div
                    v-if="getErrorFromValidator(v.editedDatafield.tableFormat)"
                    class="error"
                >
                    {{ getErrorFromValidator(v.editedDatafield.tableFormat) }}
                </div>
            </div>
        </div>

        <div
            id="x_fielddata"
            v-if="getTypeLabel(editedDatafield) == 'options'"
        >
            <h3>{{ trans('product_types.datafield_edit_popup.field_options') }}</h3>
            <table>
                <thead>
                    <tr>
                        <th id="th_datafieldoptionId">{{ trans('product_types.datafield_edit_popup.table.id') }}</th>
                        <th id="th_codeId">{{ trans('product_types.datafield_edit_popup.table.key') }}</th>
                        <th id="th_cncCodeId">{{ trans('product_types.datafield_edit_popup.table.cnc_code_id') }}</th>
                        <th id="th_dataoption">{{ trans('product_types.datafield_edit_popup.table.data_option') }}</th>
                        <th id="th_order">{{ trans('product_types.datafield_edit_popup.table.order') }}</th>
                        <th id="th_image">{{ trans('product_types.datafield_edit_popup.table.image') }}</th>
                        <th id="th_paintAreaValue">{{ trans('product_types.datafield_edit_popup.table.paint_area') }}</th>
                        <th id="th_inUse">{{ trans('product_types.datafield_edit_popup.table.active') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <datafield-option-item
                        v-for="option in editedDatafield.fielddata"
                        :option="option"
                        :languages="languages"
                        ref="oldOptions"
                        :datafield-type="editedDatafield.datatype"
                        :default-language="defaultLanguage"
                        :key="option.datafieldoptionId"
                    ></datafield-option-item>
                </tbody>
                <tbody>
                    <datafield-option-item
                        v-for="(
                            option, optionIndex
                        ) in editedDatafield.newOptions"
                        :option="option"
                        :languages="languages"
                        ref="newOptions"
                        :key="optionIndex"
                        :default-language="defaultLanguage"
                        :datafield-type="editedDatafield.datatype"
                    ></datafield-option-item>
                </tbody>
                <tbody>
                    <tr>
                        <td
                            colspan="7"
                            class="new-option-cell"
                        >
                            {{ trans('product_types.datafield_edit_popup.table.add_new_option') }}
                            <i
                                class="fa fa-plus"
                                id="dataField_addOption"
                                @click="createNewOption()"
                            ></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import ErrorHelper from '../../helpers/ErrorHelper';
import DatafieldOptionItem from './../productTypes/DatafieldOptionItem';
import TranslationsEdit from './../TranslationsEdit';
import TextInput from '../TextInput.vue';
import localization from '../../mixins/localization';

export default {
    props: [
        'editedDatafield',
        'v',
        'dataTypes',
        'subDataTypes',
        'isTableType',
        'getTypeLabel',
        'languages',
        'dataTypeOptions',
        'createNewOption',
        'defaultLanguage',
    ],
    mixins: [localization],
    computed: {
        subDataTypeOptions() {
            const options = [{ value: null, label: '---' }];

            const datatype = this.editedDatafield?.datatype;

            if (this.subDataTypes && datatype) {
                const actualOptions = this.subDataTypes[datatype];
                if (actualOptions) {
                    Object.entries(actualOptions)
                        .map((x) => ({ value: x[0], label: x[1] }))
                        .forEach((x) => options.push(x));
                    return options;
                }
            }

            return options;
        },
        showSubDataType() {
            return this.subDataTypeOptions.length > 1;
        },
        defaultValueOptions() {
            const options = (
                this.editedDatafield ? this.editedDatafield.fielddata || [] : []
            )
                .filter((x) => x.inUse)
                .map((x) => ({
                    label: x.translations.dataoption[this.defaultLanguage],
                    value: x.datafieldoptionId,
                }));

            return [{ label: '', value: null }, ...options];
        },
    },
    methods: {
        getErrorFromValidator: ErrorHelper.getErrorFromValidator,
    },
    components: {
        'datafield-option-item': DatafieldOptionItem,
        'translations-edit': TranslationsEdit,
        'text-input': TextInput,
    },
};
</script>
